<template>
    <Teleport to="body">
  
      <Transition name="ai-generating-fade">
  
        <div class="ai-generating-overlay">
  
          <div class="ai-generating-card">
  
            <!-- =================================================
                 BACKGROUND DECOR
            ================================================== -->
  
            <div class="ambient-glow glow-one"></div>
            <div class="ambient-glow glow-two"></div>
  
            <div class="grid-decoration"></div>
  
  
            <!-- =================================================
                 AI ORBIT
            ================================================== -->
  
            <div class="ai-visual">
  
              <div class="orbit orbit-large">
                <span></span>
              </div>
  
              <div class="orbit orbit-small">
                <span></span>
              </div>
  
              <div class="ai-core">
  
                <div class="core-glow"></div>
  
                <svg
                  viewBox="0 0 32 32"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="
                      M16 4
                      L18.3 11.7
                      L26 14
                      L18.3 16.3
                      L16 24
                      L13.7 16.3
                      L6 14
                      L13.7 11.7
                      L16 4Z
                    "
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linejoin="round"
                  />
  
                  <path
                    d="
                      M25 21
                      L26 24
                      L29 25
                      L26 26
                      L25 29
                      L24 26
                      L21 25
                      L24 24
                      L25 21Z
                    "
                    stroke="currentColor"
                    stroke-width="1.3"
                    stroke-linejoin="round"
                  />
                </svg>
  
              </div>
  
            </div>
  
  
            <!-- =================================================
                 BADGE
            ================================================== -->
  
            <div class="ai-generating-badge">
  
              <span class="badge-dot"></span>
  
              AI работает
  
            </div>
  
  
            <!-- =================================================
                 CURRENT STEP
            ================================================== -->
  
            <div class="step-content">
  
              <Transition
                name="step-change"
                mode="out-in"
              >
  
                <div
                  :key="currentStepIndex"
                  class="step-content-inner"
                >
  
                  <span class="step-number">
                    Этап
                    {{ currentStepIndex + 1 }}
                    из
                    {{ steps.length }}
                  </span>
  
                  <h2 class="step-title">
                    {{ currentStep.title }}
                  </h2>
  
                  <p class="step-description">
                    {{ currentStep.description }}
                  </p>
  
                </div>
  
              </Transition>
  
            </div>
  
  
            <!-- =================================================
                 VACANCY CONTEXT
            ================================================== -->
  
            <div
              v-if="vacancyTitle || companyName"
              class="vacancy-context"
            >
  
              <div class="vacancy-context-icon">
  
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <rect
                    x="4"
                    y="6"
                    width="16"
                    height="14"
                    rx="3"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
  
                  <path
                    d="M9 6V5C9 3.9 9.9 3 11 3H13C14.1 3 15 3.9 15 5V6"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
  
                  <path
                    d="M4 11H20"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
  
              </div>
  
  
              <div class="vacancy-context-text">
  
                <span
                  v-if="vacancyTitle"
                  class="vacancy-context-title"
                >
                  {{ vacancyTitle }}
                </span>
  
                <span
                  v-if="companyName"
                  class="vacancy-context-company"
                >
                  {{ companyName }}
                </span>
  
              </div>
  
            </div>
  
  
            <!-- =================================================
                 STAGE INDICATORS
            ================================================== -->
  
            <div class="stage-list">
  
              <div
                v-for="(step, index) in steps"
                :key="step.title"
                class="stage-item"
                :class="{
                  completed:
                    index < currentStepIndex,
  
                  active:
                    index === currentStepIndex
                }"
              >
  
                <div class="stage-point">
  
                  <svg
                    v-if="index < currentStepIndex"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M4 8.2L6.6 10.8L12 5.5"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
  
                  <span
                    v-else-if="
                      index ===
                      currentStepIndex
                    "
                    class="stage-pulse"
                  ></span>
  
                </div>
  
  
                <div
                  v-if="
                    index <
                    steps.length - 1
                  "
                  class="stage-line"
                ></div>
  
              </div>
  
            </div>
  
  
            <!-- =================================================
                 FOOTER
            ================================================== -->
  
            <div class="ai-generating-footer">
  
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
  
              <span>
                Письмо можно будет проверить
                перед отправкой
              </span>
  
            </div>
  
          </div>
  
        </div>
  
      </Transition>
  
    </Teleport>
  </template>
  
  
  <script setup>
  
  import {
    computed,
    onBeforeUnmount,
    onMounted,
    ref
  } from "vue";
  
  
  defineProps({
  
    vacancyTitle: {
      type: String,
      default: ""
    },
  
    companyName: {
      type: String,
      default: ""
    }
  
  });
  
  
  /* =========================================================
     STEPS
  ========================================================= */
  
  const steps = [
  
    {
      title:
        "Анализируем вакансию",
  
      description:
        "Изучаем требования, задачи и ключевые ожидания работодателя."
    },
  
    {
      title:
        "Изучаем ваше резюме и профиль",
  
      description:
        "Ищем опыт и навыки, которые лучше всего подходят этой компании."
    },
  
    {
      title:
        "Сопоставляем ваш опыт",
  
      description:
        "Определяем самые сильные совпадения между вашим профилем и вакансией."
    },
  
    {
      title:
        "Формируем аргументы",
  
      description:
        "Выбираем, на чём лучше сделать акцент в обращении к работодателю."
    },
  
    {
      title:
        "Готовим письмо",
  
      description:
        "AI собирает персональное сопроводительное письмо специально для этой компании."
    },
  
    {
      title:
        "Финализируем текст",
  
      description:
        "Проверяем структуру и формулировки. Осталось совсем немного."
    }
  
  ];
  
  
  const currentStepIndex =
    ref(0);
  
  
  const currentStep =
    computed(
      () =>
        steps[
          currentStepIndex.value
        ]
    );
  
  
  /* =========================================================
     STEP ROTATION
  ========================================================= */
  
  let stepTimer =
    null;
  
  
  function startSteps() {
  
    stepTimer =
      window.setInterval(
        () => {
  
          /*
           * Доходим до последнего этапа
           * и остаёмся на нём до завершения
           * реального API-запроса.
           */
  
          if (
            currentStepIndex.value <
            steps.length - 1
          ) {
  
            currentStepIndex.value +=
              1;
  
            return;
          }
  
  
          window.clearInterval(
            stepTimer
          );
  
          stepTimer =
            null;
  
        },
        1500
      );
  
  }
  
  
  onMounted(
    () => {
  
      startSteps();
  
    }
  );
  
  
  onBeforeUnmount(
    () => {
  
      if (stepTimer) {
  
        window.clearInterval(
          stepTimer
        );
  
      }
  
    }
  );
  
  </script>
  
  
  <style scoped>
  
  /* =========================================================
     OVERLAY
  ========================================================= */
  
  .ai-generating-overlay {
    position:
      fixed;
  
    inset:
      0;
  
    z-index:
      100001;
  
    display:
      flex;
  
    align-items:
      center;
  
    justify-content:
      center;
  
    box-sizing:
      border-box;
  
    padding:
      24px;
  
    background:
      rgba(
        11,
        16,
        24,
        0.46
      );
  
    backdrop-filter:
      blur(10px);
  
    -webkit-backdrop-filter:
      blur(10px);
  }
  
  
  /* =========================================================
     CARD
  ========================================================= */
  
  .ai-generating-card {
    position:
      relative;
  
    width:
      min(
        480px,
        calc(100vw - 32px)
      );
  
    overflow:
      hidden;
  
    box-sizing:
      border-box;
  
    padding:
      36px
      34px
      28px;
  
    background:
      rgba(
        255,
        255,
        255,
        0.985
      );
  
    border:
      1px solid
      rgba(
        28,
        44,
        66,
        0.08
      );
  
    border-radius:
      26px;
  
    box-shadow:
      0
      34px
      90px
      rgba(
        15,
        23,
        42,
        0.25
      );
  }
  
  
  /* =========================================================
     AMBIENT
  ========================================================= */
  
  .ambient-glow {
    position:
      absolute;
  
    pointer-events:
      none;
  
    border-radius:
      50%;
  
    filter:
      blur(12px);
  }
  
  
  .glow-one {
    top:
      -100px;
  
    left:
      -70px;
  
    width:
      240px;
  
    height:
      240px;
  
    background:
      radial-gradient(
        circle,
        rgba(
          62,
          137,
          244,
          0.13
        ),
        transparent 70%
      );
  }
  
  
  .glow-two {
    right:
      -80px;
  
    bottom:
      -100px;
  
    width:
      260px;
  
    height:
      260px;
  
    background:
      radial-gradient(
        circle,
        rgba(
          126,
          91,
          224,
          0.12
        ),
        transparent 70%
      );
  }
  
  
  /* =========================================================
     GRID DECORATION
  ========================================================= */
  
  .grid-decoration {
    position:
      absolute;
  
    inset:
      0;
  
    pointer-events:
      none;
  
    opacity:
      0.23;
  
    background-image:
      linear-gradient(
        rgba(
          68,
          94,
          130,
          0.03
        )
        1px,
        transparent
        1px
      ),
      linear-gradient(
        90deg,
        rgba(
          68,
          94,
          130,
          0.03
        )
        1px,
        transparent
        1px
      );
  
    background-size:
      24px
      24px;
  
    mask-image:
      linear-gradient(
        to bottom,
        black,
        transparent 65%
      );
  }
  
  
  /* =========================================================
     AI VISUAL
  ========================================================= */
  
  .ai-visual {
    position:
      relative;
  
    width:
      108px;
  
    height:
      108px;
  
    display:
      flex;
  
    align-items:
      center;
  
    justify-content:
      center;
  
    margin:
      0
      auto
      18px;
  }
  
  
  /* =========================================================
     ORBITS
  ========================================================= */
  
  .orbit {
    position:
      absolute;
  
    border:
      1px solid
      rgba(
        82,
        114,
        215,
        0.12
      );
  
    border-radius:
      50%;
  }
  
  
  .orbit-large {
    inset:
      0;
  
    animation:
      orbit-spin
      5.5s
      linear
      infinite;
  }
  
  
  .orbit-small {
    inset:
      14px;
  
    animation:
      orbit-spin-reverse
      3.6s
      linear
      infinite;
  }
  
  
  .orbit span {
    position:
      absolute;
  
    top:
      50%;
  
    left:
      -3px;
  
    width:
      6px;
  
    height:
      6px;
  
    background:
      #557fe8;
  
    border-radius:
      50%;
  
    box-shadow:
      0
      0
      12px
      rgba(
        78,
        126,
        234,
        0.65
      );
  }
  
  
  .orbit-small span {
    top:
      8px;
  
    left:
      auto;
  
    right:
      7px;
  
    width:
      5px;
  
    height:
      5px;
  
    background:
      #8b68de;
  
    box-shadow:
      0
      0
      10px
      rgba(
        139,
        104,
        222,
        0.6
      );
  }
  
  
  /* =========================================================
     CORE
  ========================================================= */
  
  .ai-core {
    position:
      relative;
  
    width:
      58px;
  
    height:
      58px;
  
    display:
      flex;
  
    align-items:
      center;
  
    justify-content:
      center;
  
    color:
      #ffffff;
  
    background:
      linear-gradient(
        135deg,
        #3e91f5,
        #5574e9 52%,
        #865fda
      );
  
    border-radius:
      19px;
  
    box-shadow:
      0
      12px
      28px
      rgba(
        75,
        102,
        221,
        0.27
      );
  
    animation:
      core-breathe
      2.5s
      ease-in-out
      infinite;
  }
  
  
  .ai-core svg {
    width:
      29px;
  
    height:
      29px;
  }
  
  
  .core-glow {
    position:
      absolute;
  
    inset:
      -16px;
  
    z-index:
      -1;
  
    background:
      radial-gradient(
        circle,
        rgba(
          94,
          107,
          230,
          0.2
        ),
        transparent 68%
      );
  
    border-radius:
      50%;
  }
  
  
  /* =========================================================
     BADGE
  ========================================================= */
  
  .ai-generating-badge {
    width:
      fit-content;
  
    display:
      flex;
  
    align-items:
      center;
  
    gap:
      6px;
  
    margin:
      0
      auto
      11px;
  
    padding:
      5px
      9px;
  
    color:
      #6470c9;
  
    background:
      rgba(
        81,
        101,
        215,
        0.07
      );
  
    border:
      1px solid
      rgba(
        81,
        101,
        215,
        0.09
      );
  
    border-radius:
      999px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size:
      8px;
  
    letter-spacing:
      0.25px;
  
    text-transform:
      uppercase;
  }
  
  
  .badge-dot {
    width:
      5px;
  
    height:
      5px;
  
    background:
      #637be1;
  
    border-radius:
      50%;
  
    box-shadow:
      0
      0
      0
      4px
      rgba(
        99,
        123,
        225,
        0.1
      );
  
    animation:
      badge-pulse
      1.4s
      ease-in-out
      infinite;
  }
  
  
  /* =========================================================
     CURRENT STEP
  ========================================================= */
  
  .step-content {
    min-height:
      116px;
  
    display:
      flex;
  
    align-items:
      flex-start;
  
    justify-content:
      center;
  }
  
  
  .step-content-inner {
    width:
      100%;
  
    text-align:
      center;
  }
  
  
  .step-number {
    display:
      block;
  
    margin-bottom:
      6px;
  
    color:
      #a0a6ae;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size:
      8px;
  
    text-transform:
      uppercase;
  
    letter-spacing:
      0.45px;
  }
  
  
  .step-title {
    margin:
      0;
  
    color:
      #292e36;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size:
      21px;
  
    line-height:
      1.25;
  
    letter-spacing:
      -0.4px;
  }
  
  
  .step-description {
    max-width:
      370px;
  
    margin:
      10px
      auto
      0;
  
    color:
      #7b828b;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size:
      11px;
  
    line-height:
      1.55;
  }
  
  
  /* =========================================================
     VACANCY
  ========================================================= */
  
  .vacancy-context {
    display:
      flex;
  
    align-items:
      center;
  
    gap:
      10px;
  
    margin-top:
      8px;
  
    padding:
      11px
      12px;
  
    background:
      rgba(
        45,
        65,
        90,
        0.035
      );
  
    border:
      1px solid
      rgba(
        35,
        55,
        80,
        0.055
      );
  
    border-radius:
      12px;
  }
  
  
  .vacancy-context-icon {
    width:
      34px;
  
    height:
      34px;
  
    flex:
      0
      0
      34px;
  
    display:
      flex;
  
    align-items:
      center;
  
    justify-content:
      center;
  
    color:
      #6784ab;
  
    background:
      rgba(
        72,
        118,
        180,
        0.07
      );
  
    border-radius:
      9px;
  }
  
  
  .vacancy-context-icon svg {
    width:
      17px;
  
    height:
      17px;
  }
  
  
  .vacancy-context-text {
    min-width:
      0;
  
    display:
      flex;
  
    flex-direction:
      column;
  
    gap:
      3px;
  }
  
  
  .vacancy-context-title {
    overflow:
      hidden;
  
    color:
      #454c55;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size:
      10px;
  
    text-overflow:
      ellipsis;
  
    white-space:
      nowrap;
  }
  
  
  .vacancy-context-company {
    overflow:
      hidden;
  
    color:
      #969ca4;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size:
      9px;
  
    text-overflow:
      ellipsis;
  
    white-space:
      nowrap;
  }
  
  
  /* =========================================================
     STAGES
  ========================================================= */
  
  .stage-list {
    display:
      flex;
  
    align-items:
      center;
  
    justify-content:
      center;
  
    margin:
      23px
      0
      0;
  
    padding:
      0
      12px;
  }
  
  
  .stage-item {
    position:
      relative;
  
    flex:
      1;
  
    display:
      flex;
  
    align-items:
      center;
  }
  
  
  .stage-item:last-child {
    flex:
      0
      0
      auto;
  }
  
  
  .stage-point {
    position:
      relative;
  
    z-index:
      2;
  
    width:
      18px;
  
    height:
      18px;
  
    flex:
      0
      0
      18px;
  
    display:
      flex;
  
    align-items:
      center;
  
    justify-content:
      center;
  
    color:
      #ffffff;
  
    background:
      #e8ebef;
  
    border-radius:
      50%;
  
    transition:
      background-color 0.25s ease,
      box-shadow 0.25s ease,
      transform 0.25s ease;
  }
  
  
  .stage-point svg {
    width:
      11px;
  
    height:
      11px;
  }
  
  
  .stage-item.completed
  .stage-point {
    background:
      #5f7edc;
  }
  
  
  .stage-item.active
  .stage-point {
    background:
      #ffffff;
  
    border:
      2px solid
      #627ee4;
  
    box-sizing:
      border-box;
  
    box-shadow:
      0
      0
      0
      5px
      rgba(
        98,
        126,
        228,
        0.09
      );
  
    transform:
      scale(1.06);
  }
  
  
  .stage-pulse {
    width:
      6px;
  
    height:
      6px;
  
    background:
      #627ee4;
  
    border-radius:
      50%;
  
    animation:
      stage-pulse
      1.1s
      ease-in-out
      infinite;
  }
  
  
  .stage-line {
    height:
      2px;
  
    flex:
      1;
  
    background:
      #e8ebef;
  
    transition:
      background-color 0.25s ease;
  }
  
  
  .stage-item.completed
  .stage-line {
    background:
      linear-gradient(
        90deg,
        #5f7edc,
        #708ce0
      );
  }
  
  
  /* =========================================================
     FOOTER
  ========================================================= */
  
  .ai-generating-footer {
    display:
      flex;
  
    align-items:
      center;
  
    justify-content:
      center;
  
    gap:
      8px;
  
    margin-top:
      21px;
  
    color:
      #969ca4;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size:
      9px;
  }
  
  
  .typing-indicator {
    display:
      flex;
  
    align-items:
      center;
  
    gap:
      3px;
  }
  
  
  .typing-indicator span {
    width:
      4px;
  
    height:
      4px;
  
    background:
      #8093c5;
  
    border-radius:
      50%;
  
    animation:
      typing-dot
      1.2s
      ease-in-out
      infinite;
  }
  
  
  .typing-indicator span:nth-child(2) {
    animation-delay:
      0.15s;
  }
  
  
  .typing-indicator span:nth-child(3) {
    animation-delay:
      0.3s;
  }
  
  
  /* =========================================================
     STEP TRANSITION
  ========================================================= */
  
  .step-change-enter-active,
  .step-change-leave-active {
    transition:
      opacity
      0.22s
      ease,
      transform
      0.22s
      ease;
  }
  
  
  .step-change-enter-from {
    opacity:
      0;
  
    transform:
      translateY(7px);
  }
  
  
  .step-change-leave-to {
    opacity:
      0;
  
    transform:
      translateY(-6px);
  }
  
  
  /* =========================================================
     OVERLAY TRANSITION
  ========================================================= */
  
  .ai-generating-fade-enter-active,
  .ai-generating-fade-leave-active {
    transition:
      opacity
      0.22s
      ease;
  }
  
  
  .ai-generating-fade-enter-active
  .ai-generating-card,
  .ai-generating-fade-leave-active
  .ai-generating-card {
    transition:
      opacity
      0.24s
      ease,
      transform
      0.28s
      cubic-bezier(
        0.2,
        0.8,
        0.3,
        1
      );
  }
  
  
  .ai-generating-fade-enter-from,
  .ai-generating-fade-leave-to {
    opacity:
      0;
  }
  
  
  .ai-generating-fade-enter-from
  .ai-generating-card {
    opacity:
      0;
  
    transform:
      translateY(12px)
      scale(0.975);
  }
  
  
  .ai-generating-fade-leave-to
  .ai-generating-card {
    opacity:
      0;
  
    transform:
      translateY(6px)
      scale(0.985);
  }
  
  
  /* =========================================================
     DARK
  ========================================================= */
  
  :global(html[data-theme="dark"])
  .ai-generating-overlay {
    background:
      rgba(
        3,
        5,
        9,
        0.66
      );
  }
  
  
  :global(html[data-theme="dark"])
  .ai-generating-card {
    background:
      linear-gradient(
        145deg,
        rgba(
          35,
          38,
          45,
          0.99
        ),
        rgba(
          27,
          29,
          34,
          0.99
        )
      );
  
    border-color:
      rgba(
        255,
        255,
        255,
        0.065
      );
  
    box-shadow:
      0
      36px
      100px
      rgba(
        0,
        0,
        0,
        0.5
      );
  }
  
  
  :global(html[data-theme="dark"])
  .step-title {
    color:
      #f1f2f4;
  }
  
  
  :global(html[data-theme="dark"])
  .step-description {
    color:
      #9299a3;
  }
  
  
  :global(html[data-theme="dark"])
  .step-number {
    color:
      #686f79;
  }
  
  
  :global(html[data-theme="dark"])
  .vacancy-context {
    background:
      rgba(
        255,
        255,
        255,
        0.03
      );
  
    border-color:
      rgba(
        255,
        255,
        255,
        0.05
      );
  }
  
  
  :global(html[data-theme="dark"])
  .vacancy-context-title {
    color:
      #d6d9de;
  }
  
  
  :global(html[data-theme="dark"])
  .vacancy-context-company {
    color:
      #767d86;
  }
  
  
  :global(html[data-theme="dark"])
  .vacancy-context-icon {
    color:
      #88a9d8;
  
    background:
      rgba(
        93,
        143,
        211,
        0.08
      );
  }
  
  
  :global(html[data-theme="dark"])
  .stage-point {
    background:
      #363a41;
  }
  
  
  :global(html[data-theme="dark"])
  .stage-line {
    background:
      #34383e;
  }
  
  
  :global(html[data-theme="dark"])
  .stage-item.active
  .stage-point {
    background:
      #22252b;
  
    border-color:
      #7898ec;
  }
  
  
  :global(html[data-theme="dark"])
  .ai-generating-footer {
    color:
      #777e87;
  }
  
  
  /* =========================================================
     ANIMATIONS
  ========================================================= */
  
  @keyframes orbit-spin {
  
    to {
      transform:
        rotate(360deg);
    }
  
  }
  
  
  @keyframes orbit-spin-reverse {
  
    to {
      transform:
        rotate(-360deg);
    }
  
  }
  
  
  @keyframes core-breathe {
  
    0%,
    100% {
      transform:
        scale(1);
    }
  
    50% {
      transform:
        scale(1.045);
    }
  
  }
  
  
  @keyframes badge-pulse {
  
    0%,
    100% {
      opacity:
        0.65;
    }
  
    50% {
      opacity:
        1;
    }
  
  }
  
  
  @keyframes stage-pulse {
  
    0%,
    100% {
      transform:
        scale(0.82);
  
      opacity:
        0.65;
    }
  
    50% {
      transform:
        scale(1.15);
  
      opacity:
        1;
    }
  
  }
  
  
  @keyframes typing-dot {
  
    0%,
    60%,
    100% {
      transform:
        translateY(0);
  
      opacity:
        0.45;
    }
  
    30% {
      transform:
        translateY(-3px);
  
      opacity:
        1;
    }
  
  }
  
  
  /* =========================================================
     MOBILE
  ========================================================= */
  
  @media (max-width: 430px) {
  
    .ai-generating-overlay {
      padding:
        14px;
    }
  
  
    .ai-generating-card {
      width:
        100%;
  
      padding:
        30px
        22px
        24px;
  
      border-radius:
        22px;
    }
  
  
    .ai-visual {
      width:
        94px;
  
      height:
        94px;
  
      margin-bottom:
        16px;
    }
  
  
    .ai-core {
      width:
        54px;
  
      height:
        54px;
    }
  
  
    .step-title {
      font-size:
        19px;
    }
  
  
    .step-description {
      font-size:
        10px;
    }
  
  
    .stage-list {
      padding:
        0
        3px;
    }
  
  
    .stage-point {
      width:
        16px;
  
      height:
        16px;
  
      flex-basis:
        16px;
    }
  
  
    .ai-generating-footer {
      font-size:
        8px;
    }
  
  }
  
  
  /* =========================================================
     REDUCED MOTION
  ========================================================= */
  
  @media (
    prefers-reduced-motion:
    reduce
  ) {
  
    .orbit,
    .ai-core,
    .badge-dot,
    .stage-pulse,
    .typing-indicator span {
      animation:
        none;
    }
  
  }

  /* =========================================================
   DARK THEME
========================================================= */

html[data-theme="dark"]
.ai-generating-overlay {
  background:
    rgba(
      2,
      4,
      8,
      0.72
    );

  backdrop-filter:
    blur(12px);

  -webkit-backdrop-filter:
    blur(12px);
}


html[data-theme="dark"]
.ai-generating-card {
  background:
    linear-gradient(
      145deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0
    36px
    100px
    rgba(
      0,
      0,
      0,
      0.56
    );
}


html[data-theme="dark"]
.grid-decoration {
  opacity:
    0.28;

  background-image:
    linear-gradient(
      rgba(
        135,
        160,
        210,
        0.035
      )
      1px,
      transparent
      1px
    ),
    linear-gradient(
      90deg,
      rgba(
        135,
        160,
        210,
        0.035
      )
      1px,
      transparent
      1px
    );
}


html[data-theme="dark"]
.glow-one {
  background:
    radial-gradient(
      circle,
      rgba(
        67,
        128,
        224,
        0.17
      ),
      transparent 70%
    );
}


html[data-theme="dark"]
.glow-two {
  background:
    radial-gradient(
      circle,
      rgba(
        129,
        87,
        218,
        0.16
      ),
      transparent 70%
    );
}


html[data-theme="dark"]
.orbit {
  border-color:
    rgba(
      120,
      151,
      229,
      0.14
    );
}


html[data-theme="dark"]
.ai-generating-badge {
  color:
    #9eaef0;

  background:
    rgba(
      93,
      112,
      221,
      0.10
    );

  border-color:
    rgba(
      116,
      134,
      230,
      0.12
    );
}


html[data-theme="dark"]
.badge-dot {
  background:
    #8499f2;

  box-shadow:
    0
    0
    0
    4px
    rgba(
      132,
      153,
      242,
      0.10
    );
}


html[data-theme="dark"]
.step-number {
  color:
    #686f79;
}


html[data-theme="dark"]
.step-title {
  color:
    #f0f1f3;
}


html[data-theme="dark"]
.step-description {
  color:
    #9299a3;
}


html[data-theme="dark"]
.vacancy-context {
  background:
    rgba(
      255,
      255,
      255,
      0.032
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.vacancy-context-icon {
  color:
    #89aae0;

  background:
    rgba(
      83,
      137,
      211,
      0.09
    );
}


html[data-theme="dark"]
.vacancy-context-title {
  color:
    #d8dbe0;
}


html[data-theme="dark"]
.vacancy-context-company {
  color:
    #767d87;
}


html[data-theme="dark"]
.stage-point {
  background:
    #353940;
}


html[data-theme="dark"]
.stage-line {
  background:
    #34383f;
}


html[data-theme="dark"]
.stage-item.completed
.stage-point {
  background:
    linear-gradient(
      135deg,
      #5874d1,
      #715aca
    );
}


html[data-theme="dark"]
.stage-item.completed
.stage-line {
  background:
    linear-gradient(
      90deg,
      #5874d1,
      #6c68d4
    );
}


html[data-theme="dark"]
.stage-item.active
.stage-point {
  background:
    #202329;

  border-color:
    #819bee;

  box-shadow:
    0
    0
    0
    5px
    rgba(
      117,
      143,
      234,
      0.10
    );
}


html[data-theme="dark"]
.stage-pulse {
  background:
    #819bee;

  box-shadow:
    0
    0
    9px
    rgba(
      129,
      155,
      238,
      0.52
    );
}


html[data-theme="dark"]
.ai-generating-footer {
  color:
    #777e88;
}


html[data-theme="dark"]
.typing-indicator span {
  background:
    #7e91c9;
}
  
  </style>